import { Link } from "react-router-dom";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { cx } from "../../../utils/cx";
import { useEducationLevels, useFieldsOfStudy } from "../model";
import { useAcademicProfile } from "./model";

export const PROFILE_PATH = "/academic-profile";

const ProfileLink = ({ children }) => (
  <Link to={PROFILE_PATH} className="font-semibold underline">
    {children}
  </Link>
);

const KINDS = {
  CURRENT_LEVEL: { label: "Current level of study", mine: (p) => p?.currentLevel?.name },
  FIELD_OF_STUDY: { label: "Field of study", mine: (p) => p?.fieldOfStudy?.name },
  MIN_GRADE_PERCENT: { label: "Final grade", mine: (p) => (p?.gradePercent == null ? null : `${Number(p.gradePercent)}%`) },
};

/**
 * The rules the profile misses, one line per kind (rules of one kind are alternatives): what is
 * needed, then what the profile says, e.g. "Current level of study: needs Bachelor's degree (A0);
 * your profile says Secondary school (A2)". Rules without a kind keep their label.
 */
function useMissed(rules) {
  const { data: profile } = useAcademicProfile();
  const { data: levels = [] } = useEducationLevels();
  const { data: fields = [] } = useFieldsOfStudy();
  const nameOf = (r) => {
    if (r.kind === "CURRENT_LEVEL") return levels.find((l) => l.id === r.value)?.levelName ?? r.label;
    if (r.kind === "FIELD_OF_STUDY") return fields.find((f) => f.id === r.value)?.fieldName ?? r.label;
    if (r.kind === "MIN_GRADE_PERCENT") return `at least ${Number(r.value)}%`;
    return r.label ?? r.value;
  };
  const known = rules.filter((r) => KINDS[r.kind]);
  const byKind = [...new Set(known.map((r) => r.kind))].map((kind) => {
    const needs = known.filter((r) => r.kind === kind).map(nameOf).join(" or ");
    return `${KINDS[kind].label}: needs ${needs}; your profile says ${KINDS[kind].mine(profile) ?? "nothing yet"}`;
  });
  return [...byKind, ...rules.filter((r) => !KINDS[r.kind]).map((r) => r.label ?? r.value)];
}

function Missed({ rules, compact, className }) {
  const missed = useMissed(rules);
  return (
    <div className={cx("flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800", className)}>
      <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
      {compact ? (
        <p>
          <span className="font-semibold">You do not meet who may apply.</span> {missed.length ? missed.join(". ") : "Your profile does not meet who may apply to any programme"}
        </p>
      ) : (
        <div>
          <p className="font-semibold">You do not meet who may apply</p>
          <ul className="mt-1 list-disc pl-5">
            {missed.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <p className="mt-2">
            You can still apply; HEC will see that your profile does not meet these. If your profile is out of date, <ProfileLink>update it</ProfileLink>.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Whether the student's academic profile meets a programme's rules (`eligibility` as the API gives
 * it: eligible, profileRequired, unmetRules). Missing them does not stop the student applying; HEC
 * sees it on the application. `compact` is the one-line form for cards; `showEligible` also says so
 * when the profile meets them. The compact form has no links, as cards are links themselves.
 */
export default function EligibilityNote({ eligibility, compact = false, showEligible = false, className }) {
  if (!eligibility) return null;
  if (eligibility.profileRequired)
    return (
      <div className={cx("flex items-start gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800", className)}>
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
        <p>
          Fill in your {compact ? "academic profile" : <ProfileLink>academic profile</ProfileLink>} to apply and see whether you meet who may apply.
        </p>
      </div>
    );
  if (eligibility.eligible)
    return showEligible ? (
      <div className={cx("flex items-center gap-2 rounded-md bg-green-50 p-3 text-sm text-green-800", className)}>
        <CheckCircleIcon className="h-5 w-5 shrink-0" /> Your academic profile meets who may apply.
      </div>
    ) : null;
  // A scholarship with several programmes lists no rules: each programme says which it misses
  return <Missed rules={eligibility.unmetRules ?? []} compact={compact} className={className} />;
}
